import { FunctionComponent, PropsWithChildren } from 'react';

import { useField } from 'react-final-form';

import {
  Icons,
  LabeledRadio,
  Tooltip,
  Typography,
  Stack,
} from '@cast/design-system';

import { WorkloadResourceLimitTypeFormModel } from '../../../_types';
import { ResourceInput } from '../Resource/ResourceInput';
import { ResourceRadioGroup } from '../Resource/ResourceRadioGroup';

import { useControlsGroupState } from '@cast/shared-rff-ui';

const label = { cpu: 'CPU', memory: 'Memory' };

type Props = {
  resource: keyof typeof label;
  LabelComponent?: FunctionComponent<PropsWithChildren>;
  size: 'medium' | 'large';
};

const ResourceLimitsInput = ({
  resource,
  LabelComponent = ({ children }) => <>{children}</>,
  size,
}: Props) => {
  // if not wrapped on `RffControlsProvider` typeFieldName='resource.type'
  const { name: typeFieldName = '' } = useControlsGroupState({
    name: `${resource}.type`,
  });
  // if not wrapped on `RffControlsProvider` typeFieldName='resource.type'
  const { name: semiAutoMultiplierName = '' } = useControlsGroupState({
    name: `${resource}.semiAutoMultiplier`,
  });

  const typeFieldValue =
    useField<WorkloadResourceLimitTypeFormModel>(typeFieldName).input.value;
  const disableMultiplier = typeFieldValue !== 'MULTIPLIER';
  const disableSemiAutoMultiplier = typeFieldValue !== 'SEMI_AUTOMATIC';
  const { meta: semiAutoMultiplierMeta } = useField<number | null>(
    semiAutoMultiplierName
  );
  const semiAutoMultiplierErrorDisplayed =
    semiAutoMultiplierMeta.error && semiAutoMultiplierMeta.touched;

  const textVariant = size === 'medium' ? 'P10R' : 'P12R';
  const labelVariant = size === 'medium' ? 'P10M' : 'P12M';

  return (
    <Stack gap={8}>
      <LabelComponent>
        <Typography variant={labelVariant}>{label[resource]}</Typography>
      </LabelComponent>

      <ResourceRadioGroup resource={resource} name="type">
        <Stack alignItems="flex-start" gap={16}>
          <Stack direction="row" gap={4} alignContent="center">
            <LabeledRadio
              size="xs"
              label="Remove existing resource limits"
              labelProps={{ variant: textVariant }}
              radioProps={{
                value: 'NO_LIMIT' satisfies WorkloadResourceLimitTypeFormModel,
                testId: `${resource}-no-limit-option`,
              }}
            />

            <Tooltip
              title="Removes any existing resource limits from containers, including those specified in your workload manifest."
              placement="top"
              arrow
            >
              <Icons.Info fontSize="medium" />
            </Tooltip>
          </Stack>
          <Stack direction="row" gap={4} alignContent="center">
            <LabeledRadio
              size="xs"
              label="Keep existing resource limits"
              labelProps={{ variant: textVariant }}
              radioProps={{
                value:
                  'KEEP_LIMITS' satisfies WorkloadResourceLimitTypeFormModel,
                testId: `${resource}-keep-limits-option`,
              }}
            />

            <Tooltip
              title="While limits provide stability predictability, they may restrict workloads that need to temporarily burst beyond their allocation."
              placement="top"
              arrow
            >
              <Icons.Info fontSize="medium" />
            </Tooltip>
          </Stack>
          <Stack direction="row" gap={4} alignContent="center">
            <LabeledRadio
              size="xs"
              label="Keep original limit-to-request ratio"
              labelProps={{ variant: textVariant }}
              radioProps={{
                value:
                  'MAINTAIN_RATIO' satisfies WorkloadResourceLimitTypeFormModel,
                testId: `${resource}-maintain-ratio-option`,
              }}
            />

            <Tooltip
              title="Scales limits proportionally with recommended requests, preserving the original limit-to-request ratio. For example, if the original limit is 2× the request, the updated limit stays 2× the new request."
              placement="top"
              arrow
            >
              <Icons.Info fontSize="medium" />
            </Tooltip>
          </Stack>
          {resource === 'cpu' && (
            <Stack
              gap={31}
              direction="row"
              alignItems="center"
              mb={semiAutoMultiplierErrorDisplayed ? 8 : 0}
            >
              <Stack
                direction="row"
                flexGrow={1}
                flexWrap="nowrap"
                gap={4}
                alignContent="center"
              >
                <LabeledRadio
                  size="xs"
                  label="Semi-automatic"
                  labelProps={{ variant: textVariant, whiteSpace: 'nowrap' }}
                  radioProps={{
                    value:
                      'SEMI_AUTOMATIC' satisfies WorkloadResourceLimitTypeFormModel,
                    testId: `${resource}-semi-automatic-option`,
                  }}
                />

                <Tooltip
                  title="Sets limits as a multiple of requests when workloads already have CPU limits defined, and those existing limits are lower than the calculated result. Otherwise, limits remain unchanged."
                  placement="top"
                  arrow
                >
                  <Icons.Info fontSize="medium" />
                </Tooltip>
              </Stack>

              <ResourceInput
                resource={resource}
                name="semiAutoMultiplier"
                disabled={disableSemiAutoMultiplier}
              />
            </Stack>
          )}
          {resource === 'memory' && (
            <Stack direction="row" gap={4} alignContent="center">
              <LabeledRadio
                size="xs"
                label="Automatic"
                labelProps={{ variant: textVariant }}
                radioProps={{
                  value: 'AUTO' satisfies WorkloadResourceLimitTypeFormModel,
                  testId: `${resource}-auto-option`,
                }}
              />
              <Tooltip
                title="Automatically adjusts existing limits to 1.5x requests when they are lower than this value. If limits already exceed this, they remain unchanged."
                placement="top"
                arrow
              >
                <Icons.Info fontSize="medium" />
              </Tooltip>
            </Stack>
          )}

          <Stack direction="row" gap={24} alignItems="center">
            <Stack
              direction="row"
              flexGrow={1}
              flexWrap="nowrap"
              gap={4}
              alignContent="center"
            >
              <LabeledRadio
                size="xs"
                label="Custom multiplier"
                labelProps={{ variant: textVariant, whiteSpace: 'nowrap' }}
                radioProps={{
                  value:
                    'MULTIPLIER' satisfies WorkloadResourceLimitTypeFormModel,
                  testId: `${resource}-multiplier-option`,
                }}
              />
              <Tooltip
                title="Sets limits as a multiple of requests for all workloads, regardless of whether they currently have limits defined. Always applies the specified multiplier."
                placement="top"
                arrow
              >
                <Icons.Info fontSize="medium" />
              </Tooltip>
            </Stack>

            <ResourceInput
              resource={resource}
              name="multiplier"
              disabled={disableMultiplier}
            />
          </Stack>
        </Stack>
      </ResourceRadioGroup>
    </Stack>
  );
};

export const ResourceLimitsField = ({
  LabelComponent,
}: {
  LabelComponent?: FunctionComponent<PropsWithChildren>;
}) => {
  return (
    <Stack gap={16} data-testid="resource-limits-field">
      <ResourceLimitsInput
        resource="cpu"
        LabelComponent={LabelComponent}
        size="large"
      />

      <ResourceLimitsInput
        resource="memory"
        LabelComponent={LabelComponent}
        size="large"
      />
    </Stack>
  );
};
